import { useEffect, useState, type ReactNode } from 'react'
import { Box, Button, HStack, Text } from '@chakra-ui/react'
import { ResponsiveTable, type ResponsiveColumn } from '../../shared'
import { toaster } from '@/components/ui/toaster'
import { useFollowUpItems, useUpdateFollowUpItem } from '../../../hooks/useFollowUp'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { extractApiMessage } from '../../../lib/apiError'
import type { FollowUpItemRowDto, FollowUpKind, FollowUpStatus } from '../../../types/followUp'

// Well under the served MaxFollowUpPageSize cap; the server cap is the only gate.
const PAGE_SIZE = 25

export const CHASE_ATTEMPT_LOGGED = 'Attempt logged — still on the chase list'

export interface FollowUpItemListProps {
  campaignId: string
  kind: FollowUpKind
  customKindId?: string
  /** Defaults to Open — the worklist the organizer acts on. */
  status?: FollowUpStatus
  /** CF-1 contract: per-row extension point, rendered after Done/Cancel (P4's call UI plugs in here). */
  rowActions?: (row: FollowUpItemRowDto) => ReactNode
}

export function FollowUpItemList({ campaignId, kind, customKindId, status = 'Open', rowActions }: FollowUpItemListProps) {
  const isMobile = useIsMobile()
  const [page, setPage] = useState(1)
  const { data, isLoading, error } = useFollowUpItems(campaignId, {
    kind, customKindId, status, page, pageSize: PAGE_SIZE,
  })
  const { mutate: updateItem, isPending, variables } = useUpdateFollowUpItem(campaignId)

  // Closing the last row of a later page leaves that page past the end: step back to the last
  // page that still has rows rather than stranding the user on an empty one.
  const serverTotal = data?.total
  useEffect(() => {
    if (serverTotal !== undefined && page > 1 && (page - 1) * PAGE_SIZE >= serverTotal) {
      setPage(Math.max(1, Math.ceil(serverTotal / PAGE_SIZE)))
    }
  }, [serverTotal, page])

  const setStatus = (row: FollowUpItemRowDto, next: FollowUpStatus) => {
    updateItem({ itemId: row.id, status: next }, {
      // CF-1 P5: on a Chase row, Done / Couldn't log an attempt and the item stays Open (a chase ends only
      // when the person votes or declines), so say so — otherwise the unchanged row looks like a no-op.
      onSuccess: () => {
        if (row.kind === 'Chase' && (next === 'Done' || next === 'Couldnt')) {
          toaster.create({ title: CHASE_ATTEMPT_LOGGED, type: 'success', duration: 4000 })
        }
      },
      onError: (err) => toaster.create({
        title: extractApiMessage(err, 'Could not update the follow-up.'), type: 'error', duration: 5000,
      }),
    })
  }

  const btnSize = isMobile ? { minH: '44px', minW: '44px' } : {}
  const busy = (row: FollowUpItemRowDto, s: FollowUpStatus) =>
    isPending && variables?.itemId === row.id && variables?.status === s

  const columns: ResponsiveColumn<FollowUpItemRowDto>[] = [
    { header: 'Name', cell: (r) => r.displayName, mobileRole: 'title' },
    { header: 'Address', cell: (r) => r.address ?? '—', mobileRole: 'meta' },
    { header: 'Phone', cell: (r) => r.phone ?? '—', mobileRole: 'meta' },
    { header: 'Note', cell: (r) => r.note ?? '', mobileRole: 'desktop-only' },
    {
      header: 'Actions',
      mobileRole: 'badge',
      cell: (r) => (
        <HStack gap={2} flexWrap="wrap" onClick={(e) => e.stopPropagation()}>
          {r.status === 'Open' && (
            <>
              <Button size="sm" variant="outline" colorPalette="brand" {...btnSize}
                loading={busy(r, 'Done')} disabled={isPending && variables?.itemId === r.id}
                onClick={() => setStatus(r, 'Done')}>Done</Button>
              <Button size="sm" variant="ghost" {...btnSize}
                loading={busy(r, 'Cancelled')} disabled={isPending && variables?.itemId === r.id}
                onClick={() => setStatus(r, 'Cancelled')}>Cancel</Button>
            </>
          )}
          {rowActions?.(r)}
        </HStack>
      ),
    },
  ]

  if (error) {
    return <Text color="danger.fg" fontSize="sm">{extractApiMessage(error, 'Could not load follow-ups.')}</Text>
  }

  const total = data?.total ?? 0
  const first = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const last = Math.min(page * PAGE_SIZE, total)

  return (
    <Box>
      <ResponsiveTable
        columns={columns}
        rows={data?.items ?? []}
        keyFn={(r) => r.id}
        isLoading={isLoading}
        emptyMessage="Nothing open here."
        bare
      />
      {(page > 1 || total > PAGE_SIZE) && (
        <HStack justify="space-between" mt={3} gap={2}>
          <Text fontSize="sm" color="muted">{first}–{last} of {total}</Text>
          <HStack gap={2}>
            <Button size="sm" variant="outline" {...btnSize} disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}>Previous</Button>
            <Button size="sm" variant="outline" {...btnSize} disabled={last >= total}
              onClick={() => setPage(p => p + 1)}>Next</Button>
          </HStack>
        </HStack>
      )}
    </Box>
  )
}
